import React, { useState, useEffect, useRef } from 'react'
import gql from "graphql-tag"
import { MentionsInput, Mention } from 'react-mentions'
import { useQuery } from '@apollo/client'
import { Link, useSearchParams, useNavigate } from "react-router-dom"
import ReactMarkdown from "react-markdown"
import { Divider } from "@material-ui/core"
import { Nav, NavItem, NavLink, TabContent, TabPane, Form, Input, FormGroup, Button, Label, Card, CardHeader, CardBody, CardText, CardFooter, Row, Col, Modal, ModalHeader, ModalBody } from "reactstrap"
import Skeleton from "react-loading-skeleton"
import moment from 'moment-timezone'
import RepAvatar from '../Material/RepAvatar'
import { RepsQuery, TaskQuery, AllTaskCommentsQuery } from "./Queries"
import { CreateTaskCommentMutation, CreateTaskChecklistMutation, UpdateTaskChecklistMutation, UpdateTaskMutation } from "./Mutations"
import withApolloClient from "../withApolloClient"
import "./TaskDetailsSidePanel.css"
import DatetimeRenderer from "../Material/DatetimeRenderer"
import TaskArchievePopover from './TaskComponents/TaskArchievePopover'
import EditCommentPopover from './TaskComponents/EditCommentPopover'
import ReactionsPopup from "./TaskComponents/ReactionsPopup"


const UpdateTaskCommentMutation = gql`
  mutation UpdateTaskComment($input: UpdateTaskCommentMutationInput!) {
    updateTaskComment(input: $input) {
      ok
      errors {
        messages
      }
    }
  }
`

function TaskDetailsSidePanel({ ...props }) {
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()
    const taskId = searchParams.get('task_id')
    const [activeTab, setActiveTab] = useState("#Updates")
    const [newChecklistTitle, setNewChecklistTitle] = useState("")

    const { loading: taskLoading, error, data, refetch: refetchTaskQuery } = useQuery(TaskQuery, {
        variables: { id: taskId },
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
        skip: !taskId, // Skip the query if taskId is not available
    })
    const task = data?.task

    const { loading: commentsLoading, error: commentsError, data: commentsData, refetch: refetchTaskCommentsQuery } = useQuery(AllTaskCommentsQuery, {
        variables: { taskId: taskId },
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
        skip: !taskId,
    })
    const taskCommentsData = commentsData?.allTaskComments
    const [showEditIcon, setShowEditIcon] = useState(false)
    const [isEditingTitle, setIsEditingTitle] = useState(false)
    const [editedTitle, setEditedTitle] = useState(task?.title || "")
    const [isEditingDescription, setIsEditingDescription] = useState(false)
    const [editedDescription, setEditedDescription] = useState("")
    const [showEditDescriptionIcon, setShowEditDescriptionIcon] = useState(false)
    const [isSaving, setIsSaving] = useState(false)

    const toggleChecklistItem = (checklistId, value) => {
        try {
            const input = {
                checklistId: checklistId, isCompleted: value,
            }

            props.apolloClient.mutate({
                mutation: UpdateTaskChecklistMutation,
                variables: { input },
                refetchQueries: ["Task"],
            }).then((result) => {
                if (!result || !result.data || !result.data.updateTaskChecklist || !result.data.updateTaskChecklist.ok) { throw new Error("Error updating checklist item") }
            }).catch((error) => {
                console.log("Error updating checklist item", error)
            })
        } catch (error) {
            console.log("Error updating checklist item", error)
        }
    }

    const createChecklist = () => {
        try {
            const input = {
                taskId: task.id,
                title: newChecklistTitle,
            }

            props.apolloClient.mutate({
                mutation: CreateTaskChecklistMutation,
                variables: { input },
                refetchQueries: ["Task"],
            }).then((result) => {
                if (!result || !result.data || !result.data.createTaskChecklist || !result.data.createTaskChecklist.ok) { throw new Error("Error creating checklist item") }
                setNewChecklistTitle("")
            }).catch((error) => {
                console.log("Error creating checklist item 1", error)
            })
        } catch (error) {
            console.log("Error creating checklist item 2", error)
        }
    }

    if (taskLoading && !task) {
        return (
            <div className="TaskDetailsSidePanelContainer">
                <div className="TaskDetailsSidePanel show">
                    <Skeleton height={50} width={200} />
                    <Skeleton count={3} height={30} />
                    <Skeleton height={150} />
                    <Skeleton count={2} height={20} />
                </div>
            </div>
        )
    }

    const handleSaveTaskField = async (field, value) => {
        if (!field) return
        setIsSaving(true)

        const input = {
            taskId,
            [field]: value,
        }

        try {
            await props.apolloClient.mutate({
                mutation: UpdateTaskMutation,
                variables: { input },
                refetchQueries: ["Task"],
            })

            if (field === "title") setIsEditingTitle(false)
            if (field === "description") setIsEditingDescription(false)
        } catch (error) {
            console.error(`Error saving ${field}:`, error)
        }
        finally {
            setIsSaving(false)
        }
    }

    const clearTaskId = (e) => {
        e.preventDefault()
        const next = new URLSearchParams(searchParams)
        next.delete('task_id')
        setSearchParams(next, { replace: true }) // keep other params, replace history
    }


    return (
        <div>
            <div className="TaskDetailsSidePanelContainer" >
                <div className={`TaskDetailsSidePanel show`}>
                    {/* on click of the close button, go back to the previous page */}
                    {/* <Link to={`/boards${boardId ? "/" + boardId : ""}/tasks/`} className="btn btn-outline-dark"> */}
                    <div className="TaskDetailsSidePanelHeader d-flex justify-content-between align-items-center mb-3">
                        <Link onClick={clearTaskId} className="close-button">
                            <i className="fa fa-times mb-3" aria-hidden="true"></i>
                        </Link>
                        <TaskArchievePopover taskId={task?.id} onArchived={clearTaskId} />
                    </div>
                    <div className="editable-title-container d-inline-flex align-items-center" onMouseEnter={() => setShowEditIcon(true)} onMouseLeave={() => { setShowEditIcon(false); if (isEditingTitle) handleSaveTaskField("title", editedTitle) }}>
                        {!isEditingTitle ? (
                            <>
                                <h3 className="task-title">{task?.title || "Untitled"}</h3>

                                {showEditIcon && (
                                    <span className="edit-icon-box ml-2" onClick={() => { setEditedTitle(task?.title || ""); setIsEditingTitle(true) }}><i className="fa fa-pencil-square-o"></i></span>
                                )}
                            </>
                        ) : (
                            <div className="d-flex align-items-center">
                                <input type="text" className="form-control title-edit-input" value={editedTitle} autoFocus onChange={(e) => setEditedTitle(e.target.value)} onBlur={() => handleSaveTaskField("title", editedTitle)} onKeyDown={(e) => e.key === "Enter" && handleSaveTaskField("title", editedTitle)} />
                                {isSaving && <span className="ml-2"><i className="fa fa-spinner fa-spin"></i></span>}
                            </div>
                        )}
                    </div>

                    <br />

                    <div className="editable-title-container d-inline-flex align-items-start" onMouseEnter={() => setShowEditDescriptionIcon(true)} onMouseLeave={() => { setShowEditDescriptionIcon(false); if (isEditingDescription) handleSaveTaskField("description", editedDescription) }}>
                        {!isEditingDescription ? (
                            <>
                                <p className="task-description mb-0">{task?.description || "No description"}</p>

                                {showEditDescriptionIcon && (
                                    <span className="edit-icon-box ml-2" onClick={() => { setEditedDescription(task?.description || ""); setIsEditingDescription(true) }}><i className="fa fa-pencil-square-o"></i></span>
                                )}
                            </>
                        ) : (
                            <div className="d-flex align-items-center">
                                <textarea className="form-control title-edit-input" style={{ width: "500px" }} value={editedDescription} autoFocus onChange={(e) => setEditedDescription(e.target.value)} onBlur={() => handleSaveTaskField("description", editedDescription)} onKeyDown={(e) => { if (e.key === "Enter" && e.ctrlKey) handleSaveTaskField("description", editedDescription) }} />
                                {isSaving && <span className="ml-2"><i className="fa fa-spinner fa-spin"></i></span>}
                            </div>
                        )}
                    </div>
                    {/* <p className="text-muted text-xs">
                        <small>
                            In {task?.board ? task?.board.title : 'Board'}
                            {task?.dueDate && <span>  ·  Due date: {moment(task?.dueDate).tz("America/New_York").format('dddd, MMMM Do YYYY')}</span>}
                            {task?.createdBy && <span>  ·  Created by: {task?.createdBy.firstName ? task?.createdBy.firstName + " " + task?.createdBy.lastName : task?.createdBy.username}</span>}
                            {task?.driver && <span>  ·  Driver: {task?.driver.name} <Link color="primary" to={'/driver/' + task?.driver.id} target="_blank"><i className={"fa fa-external-link-square"}></i></Link></span>}
                            {task?.vehicle && <span>  ·  Car: {task?.vehicle.year} {task?.vehicle.make} {task?.vehicle.model} - {task?.vehicle.vin} <Link color="primary" to={'/car/' + task?.vehicle.id} target="_blank"><i className={"fa fa-external-link-square"}></i></Link></span>}
                        </small>
                    </p> */}
                    <Nav tabs className="mt-4">
                        <NavItem className={activeTab === "#Updates" ? "active" : "inactive"}><NavLink onClick={() => setActiveTab("#Updates")} active={activeTab === "#Updates"} style={{ cursor: "pointer" }}>Updates</NavLink></NavItem>
                        <NavItem className={activeTab === "#Activity" ? "active" : "inactive"}><NavLink onClick={() => setActiveTab("#Activity")} active={activeTab === "#Activity"} style={{ cursor: "pointer" }}>Activity Log</NavLink></NavItem>
                        {/* <NavItem>
                            <NavLink onClick={() => setActiveTab("#Checklist")} active={activeTab === "#Checklist"} style={{ cursor: "pointer" }}>
                                Checklist
                                {task?.taskchecklistSet && task?.taskchecklistSet.edges && task?.taskchecklistSet.edges.filter(checklist => !checklist.node.completedOn).length > 0 &&
                                    <span className="badge badge-pill badge-danger ml-2">
                                        {task?.taskchecklistSet && task?.taskchecklistSet.edges && task?.taskchecklistSet.edges.filter(checklist => !checklist.node.completedOn).length}
                                    </span>
                                }
                            </NavLink>
                        </NavItem> */}
                    </Nav>
                    <TabContent activeTab={activeTab}>
                        <TabPane tabId="#Updates">
                            <NewCommentForm commentId={task?.id} {...props} />
                            <Divider className="my-4" />
                            {taskCommentsData && taskCommentsData?.edges && taskCommentsData?.edges?.map((comment, index) => {
                                return <CommentCard
                                    key={index}
                                    comment={comment.node}
                                    {...props} />
                            })}
                        </TabPane>
                        <TabPane tabId="#Activity">
                            <Divider className="my-4" />
                            <ul className="list-unstyled">
                                {task?.history && task?.history.map((item, index) => (
                                    <li key={index} className="mb-2">
                                        <Row className="align-items-center">
                                            <Col className="text-left">
                                                <p className="mb-0">{item.date ? moment(item.date).tz("America/New_York").format('lll') : ''}</p>
                                            </Col>
                                            <Col className="text-left">
                                                <p className="mb-0">{item.comment}</p>
                                            </Col>
                                            <Col className="text-left">
                                                <p className="mb-0">{item.user}</p>
                                            </Col>

                                        </Row>
                                        <hr className="my-1" />
                                    </li>
                                ))}
                            </ul>
                        </TabPane>
                        <TabPane tabId="#Checklist">
                            <Divider className="my-4" />
                            <ul className="list-unstyled">
                                {task && task?.taskchecklistSet && task?.taskchecklistSet.edges && task?.taskchecklistSet.edges.length > 0 &&
                                    task?.taskchecklistSet.edges.map((checklist, index) => (
                                        checklist = checklist.node,
                                        <li key={index} className="mb-2">
                                            <Row className="align-items-center">
                                                <Col className="text-left">
                                                    <FormGroup check>
                                                        <Input
                                                            id={checklist.id}
                                                            type="checkbox"
                                                            checked={checklist.completedOn}
                                                            onChange={() => toggleChecklistItem(checklist.id, !checklist.completedOn)}
                                                        />
                                                        {' '}
                                                        <Label check for={checklist.id}>
                                                            <span style={{ textDecoration: checklist.completedOn ? "line-through" : "none" }}>{checklist.title}</span>
                                                        </Label>
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                        </li>
                                    ))}
                                {/* New checklist input text with submit button on the right */}
                                <li className="mt-4">
                                    <Row className="align-items-center">
                                        <Col className="text-left">
                                            <FormGroup>
                                                <Input
                                                    value={newChecklistTitle}
                                                    onChange={(e) => setNewChecklistTitle(e.target.value)}
                                                    type="text" name="checklist"
                                                    id="checklist"
                                                    placeholder="Add a new checklist item"
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col className="text-right">
                                            <Button onClick={createChecklist} color="primary">Add</Button>
                                        </Col>
                                    </Row>
                                </li>
                            </ul>
                        </TabPane>
                    </TabContent>

                </div>
            </div>
        </div>
    )
}


export function CommentCard({ comment, ...props }) {
    const [isEditing, setIsEditing] = useState(false)
    const [editedComment, setEditedComment] = useState(comment.comment)
    const [saving, setSaving] = useState(false)
    // const [editComment, setEditComment] = useState(null)
    // const [isEditOpen, setIsEditOpen] = useState(false)
    const [previewUrl, setPreviewUrl] = useState(null)
    const [previewType, setPreviewType] = useState(null)
    // const [showReactionPopup, setShowReactionPopup] = useState(null)

    if (!comment) {
        return null
    }

    const handleCommentEdit = (fullComment) => {
        if (!fullComment) return
        setEditedComment(fullComment.comment)   // keep old value
        setIsEditing(true)
    }

    const handleSave = async () => {
        try {
            setSaving(true)

            const input = {
                commentId: comment.id,
                comment: editedComment,
            }

            await props.apolloClient.mutate({
                mutation: UpdateTaskCommentMutation,
                variables: { input },
                refetchQueries: ["Task"],
            })

            setIsEditing(false)
        } catch (e) {
            console.log("Save error", e)
        } finally {
            setSaving(false)
        }
    }

    const openPreview = (attachment) => {
        const ext = attachment.extension.toLowerCase()

        if (["png", "jpeg", "jpg", "tiff", "webp", "gif"].includes(ext)) {
            setPreviewType("image")
        } else if (["mp4"].includes(ext)) {
            setPreviewType("video")
        } else if (["mp3"].includes(ext)) {
            setPreviewType("audio")
        } else if (["pdf"].includes(ext)) {
            setPreviewType("pdf")
        }

        setPreviewUrl(attachment.url)
    }

    return (
        <Card className="my-4 CommentCard">
            <CardHeader>
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <span className="text-secondary"><RepAvatar rep={comment.createdBy} keepOpen /></span>
                    </div>
                    <div><DatetimeRenderer datetime={comment.dateAdded} /></div>
                </div>
            </CardHeader>
            <CardBody>
                {isEditing ? (
                    <div>
                        <textarea className="form-control" value={editedComment} rows={4} onChange={(e) => setEditedComment(e.target.value)} />
                        <div className="mt-3 d-flex gap-2">
                            <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>{saving ? "Saving..." : "Save"}</button>&nbsp;&nbsp;
                            <button className="btn btn-secondary btn-sm" onClick={() => {
                                setIsEditing(false)
                                setEditedComment(comment.comment)
                            }}>Cancel</button>
                        </div>
                    </div>
                ) : (
                    <CardText className='font'>
                        <ReactMarkdown children={comment.comment} />
                    </CardText>
                )}

                {comment.taskcommentattachmentSet && comment.taskcommentattachmentSet.edges && comment.taskcommentattachmentSet.edges.length > 0 && (
                    <div>
                        <Divider className="my-3" />
                        <div className="attachment-row">
                            {comment.taskcommentattachmentSet.edges.map((item, index) => {
                                let attachment = item.node
                                const openPreview = (type) => {
                                    setPreviewUrl(attachment.url)
                                    setPreviewType(type)
                                }
                                return (
                                    <div key={index} className="attachment-box" onClick={() => openPreview(["png", "jpeg", "jpg", "tiff", "webp", "gif"].includes(attachment.extension) ? "image" : ["mp4"].includes(attachment.extension) ? "video" : ["mp3"].includes(attachment.extension) ? "audio" : ["pdf"].includes(attachment.extension) ? "pdf" : null)}                                    >
                                        <div className="attachment-name">{attachment.name.length > 20 ? attachment.name.substring(0, 20) + "..." : attachment.name}</div>
                                        {["png", "jpeg", "jpg", "tiff", "webp", "gif"].includes(attachment.extension) && (
                                            <img className="attachment-thumbnail" src={attachment.url} alt={attachment.name} />
                                        )}
                                        {["mp4"].includes(attachment.extension) && (
                                            <video className="attachment-thumbnail" src={attachment.url} />
                                        )}

                                        {["mp3"].includes(attachment.extension) && (
                                            <audio className="attachment-audio" src={attachment.url} controls />
                                        )}

                                        {["pdf"].includes(attachment.extension) && (
                                            <div className="attachment-pdf"><i className="fa fa-file-pdf-o fa-3x text-danger"></i></div>
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                )}

                <Modal isOpen={!!previewUrl} toggle={() => setPreviewUrl(null)} size="lg">
                    <ModalHeader toggle={() => setPreviewUrl(null)}>Preview</ModalHeader>
                    <ModalBody>
                        {{
                            image: <img src={previewUrl} alt="Preview" style={{ width: "100%" }} />,
                            video: <video src={previewUrl} style={{ width: "100%" }} controls />,
                            audio: <audio src={previewUrl} style={{ width: "100%" }} controls />,
                            pdf: <iframe src={previewUrl} style={{ width: "100%", height: "80vh" }} />
                        }[previewType] || null}
                    </ModalBody>
                </Modal>
            </CardBody>
            <CardFooter>
                {/* Like, reply buttons  */}
                <div className="d-flex justify-content-between align-items-center">
                    <ReactionsPopup commentId={comment.id} />
                    <EditCommentPopover comment={comment} onEdit={handleCommentEdit} onArchive={"onArchive"} />
                </div>
            </CardFooter>
        </Card>
    )
}

export function NewCommentForm({ commentId, ...props }) {
    const [commentBody, setCommentBody] = useState("")
    const [error, setError] = useState("")
    const [loading, setLoading] = useState(false)
    const [files, setFiles] = useState([])

    let inp = useRef()
    useEffect(() => {
        if (!inp && !inp.current) return
        // inp.current.focus()
        return () => inp = null
    })

    // Submit when user presses Shift + Enter
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "Enter" && e.shiftKey) {
                createTaskComment(e)
            }
        }
        document.addEventListener("keydown", handleKeyDown)
        return () => document.removeEventListener("keydown", handleKeyDown)
    }, [commentBody])

    // Find users with RepsQuery
    const searchReps = async (search, callBack) => {
        try {
            // const { apolloClient } = this.props
            if (!props.apolloClient) {
                throw new Error("GQL Client not found")
            }

            props.apolloClient.query({
                query: RepsQuery,
                variables: { searchTerm: search },
            }).then((result) => {
                if (!result) {
                    throw new Error("Error loading reps")
                }
                if (result.data && result.data.allReps && result.data.allReps.edges) {
                    let reps = result.data.allReps.edges.map(rep => { return { id: rep.node.id, display: rep.node.firstName ? rep.node.firstName + " " + rep.node.lastName : rep.node.username } })
                    callBack(reps)
                } else {
                    console.log("Error loading reps", result.data.allReps)
                }
            }).catch((error) => {
                console.log("Error loading reps", error)
            })
        } catch (error) {
            console.log("Error loading reps", error)
        }
    }

    // const handleFileChange = (e) => {



    const createTaskComment = async (e) => {
        e.preventDefault()
        try {
            // const { apolloClient } = this.props
            if (!props.apolloClient || !commentId) {
                throw new Error("GQL Client not found")
            }
            if (!commentBody || commentBody.length < 5) {
                setError("Comment is required or too short")
                return
            }
            setLoading(true)
            let filesInBase64 = [] // {name: "", type: "", data: ""}
            for (let i = 0; i < files.length; i++) {
                const file = files[i]
                // Convert file to base64
                const reader = new FileReader()
                reader.readAsDataURL(file)
                await new Promise((resolve, reject) => {
                    reader.onload = () => {
                        filesInBase64.push({ name: file.name, type: file.type, data: reader.result })
                        resolve()
                    }
                    reader.onerror = error => reject(error)
                })
            }

            const input = {
                taskId: commentId,
                comment: commentBody,
                files: filesInBase64
            }

            props.apolloClient.mutate({
                mutation: CreateTaskCommentMutation,
                refetchQueries: ["Task"],
                variables: { input },
            }).then((result) => {
                setLoading(false)
                if (!result) {
                    throw new Error("Error creating task update")
                }
                if (result.data && result.data.createTaskComment && result.data.createTaskComment.ok) {
                    setError("")
                    setCommentBody("")
                    setFiles([])

                } else {
                    const errorMessage = result.data && result.data.createTaskComment && result.data.createTaskComment.errors && result.data.createTaskComment.errors.messages ? result.data.createTaskComment.errors.messages[0] : "Unknown Error"
                    throw new Error(errorMessage)
                }
            }).catch((error) => {
                setError("Error creating task update" + error.toString())
                setLoading(false)
            })
        } catch (error) {
            console.log("Error creating task update", error)
            setError("Error creating task update" + error.toString())
            setLoading(false)
        }
    }

    return (
        <Form className="NewCommentForm" name="NewCommentForm" onSubmit={createTaskComment}>
            <FormGroup className="NewCommentTextAreaFormGroup">
                {/* <Label for="exampleText">Write an update</Label> */}
                <div className="MentionsInputContainer position-relative">
                    <MentionsInput placeholder="Write an update and mention someone with @" value={commentBody} onChange={(e) => setCommentBody(e.target.value)} className="text-area" inputRef={inp}>
                        <Mention
                            trigger="@"
                            data={(search, callBack) => searchReps(search, callBack)}
                            displayTransform={(id, display) => `@${display}`}
                            appendSpaceOnAdd={true}
                            renderSuggestion={(suggestion, search, highlightedDisplay) => (
                                <div className="user-suggestion">
                                    {highlightedDisplay}
                                </div>
                            )}
                        />
                    </MentionsInput>

                    <i className="fa fa-paperclip pin-icon" title="Attach a file" onClick={() => document.getElementById("hiddenFileInput").click()}></i>
                    <input type="file" id="hiddenFileInput" style={{ display: "none" }} multiple onChange={(e) => setFiles([...e.target.files])} />
                </div>

            </FormGroup>
            <div>
                {files && files.length > 0 && (
                    <div className="d-flex flex-wrap gap-3 mt-2">
                        {files.map((file, index) => {
                            const url = URL.createObjectURL(file)

                            return (
                                <div key={index} className="p-2 border rounded d-flex flex-column align-items-center" style={{ width: "120px", position: "relative" }}>
                                    {/* File name (truncate) */}
                                    <div className="text-truncate w-100 small mb-1" title={file.name}>
                                        {file.name}
                                    </div>

                                    {/* IMAGE */}
                                    {file.type.includes("image") && (
                                        <img src={url} alt={file.name} style={{ width: "100%", height: "80px", objectFit: "cover" }} />
                                    )}

                                    {/* VIDEO */}
                                    {file.type.includes("video") && (
                                        <video src={url} style={{ width: "100%", height: "80px", objectFit: "cover" }} controls />
                                    )}

                                    {/* AUDIO */}
                                    {file.type.includes("audio") && (
                                        <audio src={url} controls style={{ width: "100%" }} />
                                    )}

                                    {/* PDF */}
                                    {file.type.includes("pdf") && (
                                        <iframe src={url} style={{ width: "100%", height: "80px", border: 0 }} title={file.name} />
                                    )}
                                </div>
                            )
                        })}
                    </div>
                )}
            </div>
            <div className="update-submit-button-container">
                <Button type="submit" disabled={!commentBody || loading || !commentId}>{loading ? "Adding update..." : "Add update"}</Button>
            </div>
        </Form>
    )
}

export default withApolloClient(TaskDetailsSidePanel)
